import { Fund, NestedKeyOf } from "creso-core/types";
import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
} from "creso-core/utils";
import { format } from "date-fns";
import get from "lodash/get";
import { FundCategoryBadge } from "web-core";
import { fundInfoRowConfig } from "./config";
import { RiskCell } from "../risk-cell";

export function FundInfoCell({
  label,
  fund,
}: {
  label: string;
  fund: Fund | undefined;
}) {
  if (!fund) {
    return <span className="text-sm">N/A</span>;
  }

  const accessor = fundInfoRowConfig.find((config) => config.label === label)
    ?.accessor as NestedKeyOf<Fund>;

  if (accessor === "legal_name") {
    return (
      <div className="flex items-center gap-2">
        <AmcLogo amc_code={fund.provider_company_id ?? null} />
        <span className="text-sm">{fund.fund_name}</span>
      </div>
    );
  }

  if (accessor === "risk_level") {
    return <RiskCell value={fund.risk_level} />;
  }

  if (accessor === "assets_under_management") {
    return (
      <span className="text-sm">
        {formatAsCrore(fund[accessor] as string, 0)}
      </span>
    );
  }

  if (accessor === "expense_ratio") {
    return (
      <span className="text-sm">
        {formatAsPercentage(fund[accessor] as string)}
      </span>
    );
  }

  if (accessor === "inception_date") {
    return (
      <span className="text-sm">
        {format(fund[accessor] as string, "dd MMM yyyy")}
      </span>
    );
  }

  if (accessor === "performance.nav") {
    return (
      <span className="text-sm">
        {convertToCurrency(get(fund, accessor, "0"), 2)}
      </span>
    );
  }

  if (accessor === "broad_category_group") {
    if (!fund[accessor]) return null;
    return (
      <FundCategoryBadge category={fund[accessor] as string}>
        {fund[accessor] as string} - {fund.fund_level_category_name}
      </FundCategoryBadge>
    );
  }

  if (
    [
      "performance.return_1_month",
      "performance.return_3_month",
      "performance.return_1_year",
      "performance.return_3_year",
      "performance.return_5_year",
      "performance.return_10_year",
    ].includes(accessor)
  ) {
    return (
      <span className="text-sm">
        {formatAsPercentage(get(fund, accessor, "0") as string)}
      </span>
    );
  }

  return (
    <span className="text-sm">{get(fund, accessor, "N/A") as string}</span>
  );
}
